import React, { useState } from 'react';
import { Grid, Button, TextField, MenuItem } from '@mui/material';
import { useQueryClient } from 'react-query';
import { eventsService } from '../../../../core/services/eventsService';
import { EventGeneralSettings } from '../../../../types';
import styles from '../SchedulerSetting.module.scss';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

const SLOT_DURATION_OPTIONS = [
	{ value: '00:15', label: '15 min' },
	{ value: '00:30', label: '30 min' },
	{ value: '01:00', label: '1 hour' },
];

interface EditEventGeneralSettingsProps {
	settings: EventGeneralSettings | null;
	handleBackClick?: () => void;
	onSettingsUpdated?: () => void;
}

const EditEventGeneralSettings: React.FC<EditEventGeneralSettingsProps> = ({
	settings,
	handleBackClick,
	onSettingsUpdated,
}) => {
	const [settingsBuffer, setSettingsBuffer] = useState(() => {
		const workplaceId = getCurrentWorkplaceId();
		return {
			workplace: settings?.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : null),
			maximumReservationsPerTimeSlot: settings?.maximumReservationsPerTimeSlot ?? '',
			maxReservationSize: settings?.maxReservationSize ?? '',
			reservationSlotDuration: settings?.reservationSlotDuration ?? '',
		};
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const queryClient = useQueryClient();

	const validate = (): boolean => {
		const maximumReservationsPerTimeSlot = Number(settingsBuffer.maximumReservationsPerTimeSlot);
		const maxReservationSize = Number(settingsBuffer.maxReservationSize);

		if (!maximumReservationsPerTimeSlot || maximumReservationsPerTimeSlot < 1) {
			setError('Maximum reservations per time slot must be at least 1');
			return false;
		}
		if (!maxReservationSize || maxReservationSize < 1) {
			setError('Max reservation size must be at least 1');
			return false;
		}
		if (maxReservationSize > maximumReservationsPerTimeSlot) {
			setError('Max reservation size cannot exceed maximum reservations per time slot');
			return false;
		}
		if (!SLOT_DURATION_OPTIONS.some((o) => o.value === settingsBuffer.reservationSlotDuration)) {
			setError('Please select a valid reservation slot duration');
			return false;
		}
		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!validate()) {
			return;
		}

		try {
			setIsSubmitting(true);
			await eventsService.saveEventSettings({
					...(settings || {}),
					...settingsBuffer,
					maximumReservationsPerTimeSlot: Number(settingsBuffer.maximumReservationsPerTimeSlot),
					maxReservationSize: Number(settingsBuffer.maxReservationSize),
				});
			await queryClient.invalidateQueries(['eventSettings']);
			onSettingsUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to update settings');
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit Reservation Settings</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Max Reservations Per Time Slot"
					name="maximumReservationsPerTimeSlot"
					type="number"
					value={settingsBuffer.maximumReservationsPerTimeSlot}
					onChange={(e) =>
						setSettingsBuffer({
							...settingsBuffer,
							maximumReservationsPerTimeSlot: Number(e.target.value),
						})
					}
					placeholder="Enter maximum reservations per time slot"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1 }}
				/>

				<TextField
					label="Max Reservation Size"
					name="maxReservationSize"
					type="number"
					value={settingsBuffer.maxReservationSize}
					onChange={(e) =>
						setSettingsBuffer({
							...settingsBuffer,
							maxReservationSize: Number(e.target.value),
						})
					}
					placeholder="Enter max reservation size"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1 }}
				/>

				<TextField
					select
					label="Reservation Slot Duration"
					name="reservationSlotDuration"
					value={settingsBuffer.reservationSlotDuration}
					onChange={(e) =>
						setSettingsBuffer({
							...settingsBuffer,
							reservationSlotDuration: e.target.value,
						})
					}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					InputLabelProps={{ shrink: true }}
					SelectProps={{
						displayEmpty: true,
						renderValue: (selected: unknown): React.ReactNode => {
							const value = typeof selected === 'string' ? selected : '';
							if (!value) {
								return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>Select slot duration</span>;
							}
							return (
								SLOT_DURATION_OPTIONS.find((option) => option.value === value)?.label ?? value
							);
						},
					}}
				>
					{SLOT_DURATION_OPTIONS.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</TextField>
			</form>
		</div>
	);
};

export default EditEventGeneralSettings;
